import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from '../lib/api';

interface NominativoClienteOption {
  id: number;
  ragioneSociale: string | null;
  nome: string | null;
  cognome: string | null;
  partitaIva: string | null;
  codiceFiscale: string | null;
}

interface Props {
  societaId: number;
  value: number | null;
  onChange: (nominativoId: number | null, scelto: NominativoClienteOption | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Autocomplete per selezione nominativo cliente della società.
 * Usa GET /api/nominativi/cerca-clienti?societaId=X&q=Y
 */
export default function NominativoClientePicker({
  societaId, value, onChange, disabled, placeholder,
}: Props) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<NominativoClienteOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scelto, setScelto] = useState<NominativoClienteOption | null>(null);
  const debounceRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Se value cambia dall'esterno, carica i dati del nominativo selezionato (per mostrare l'etichetta in chiuso)
  useEffect(() => {
    if (value && (!scelto || scelto.id !== value)) {
      // @api GET /api/nominativi/:id -> {"success":"boolean","data":{"id":"number","ragioneSociale":"string","nome":"string","cognome":"string","partitaIva":"string","codiceFiscale":"string"}}
      apiFetch<{ success: boolean; data: NominativoClienteOption }>(`/api/nominativi/${value}`)
        .then(r => { if (r?.data) setScelto(r.data); })
        .catch(() => setScelto(null));
    }
    if (!value) setScelto(null);
  }, [value]); // eslint-disable-line

  const fetchOptions = useCallback(async (q: string) => {
    if (!societaId) { setOptions([]); return; }
    setLoading(true);
    try {
      // @api GET /api/nominativi/cerca-clienti?societaId=X&q=Y -> {"success":"boolean","data":[{"id":"number","ragioneSociale":"string","nome":"string","cognome":"string","partitaIva":"string","codiceFiscale":"string"}]}
      const r = await apiFetch<{ success: boolean; data: NominativoClienteOption[] }>(
        `/api/nominativi/cerca-clienti?societaId=${societaId}${q ? `&q=${encodeURIComponent(q)}` : ''}`
      );
      setOptions(r?.data || []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  // Debounce ricerca
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { fetchOptions(query); }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open, fetchOptions]);

  // Chiusura dropdown su click fuori
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const labelCorrente = scelto
    ? (scelto.ragioneSociale || `${scelto.nome || ''} ${scelto.cognome || ''}`.trim() || `#${scelto.id}`)
    : '';

  const handleScegli = (opt: NominativoClienteOption) => {
    setScelto(opt);
    setOpen(false);
    setQuery('');
    onChange(opt.id, opt);
  };

  const handleReset = () => {
    setScelto(null);
    setQuery('');
    onChange(null, null);
  };

  return (
    <div className="bop-nominativo-picker" ref={containerRef}>
      {!open && (
        <div className="bop-nominativo-picker__display">
          <input
            className="bop-input"
            value={labelCorrente}
            placeholder={placeholder || 'Seleziona cliente…'}
            readOnly
            onClick={() => !disabled && setOpen(true)}
            disabled={disabled}
          />
          {value && !disabled && (
            <button type="button" className="bop-btn bop-btn-ghost" onClick={handleReset} title="Rimuovi">×</button>
          )}
        </div>
      )}
      {open && (
        <div className="bop-nominativo-picker__dropdown">
          <input
            className="bop-input"
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca per ragione sociale, nome, cognome…"
          />
          <div className="bop-nominativo-picker__list">
            {loading && <div className="bop-nominativo-picker__empty">Caricamento…</div>}
            {!loading && options.length === 0 && (
              <div className="bop-nominativo-picker__empty">Nessun risultato</div>
            )}
            {!loading && options.map(opt => (
              <button
                key={opt.id}
                type="button"
                className="bop-nominativo-picker__item"
                onClick={() => handleScegli(opt)}
              >
                <div className="bop-nominativo-picker__item-title">
                  {opt.ragioneSociale || `${opt.nome || ''} ${opt.cognome || ''}`.trim() || `#${opt.id}`}
                </div>
                <div className="bop-nominativo-picker__item-sub">
                  {opt.partitaIva ? `P.IVA ${opt.partitaIva}` : ''}
                  {opt.partitaIva && opt.codiceFiscale ? ' — ' : ''}
                  {opt.codiceFiscale ? `C.F. ${opt.codiceFiscale}` : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
